/**
 * KB 연동 플로우 추적 훅
 * 
 * KB 연동 전체 플로우의 의도-결과를 추적
 * - kb_connect_flow_start: KB 연동 시작
 * - kb_connect_flow_success: KB 연동 성공
 * - kb_connect_flow_fail: KB 연동 실패
 * 
 * @example
 * ```tsx
 * function KBConnectPage() {
 *   const kbConnectFlow = useKBConnectFlow();
 *   
 *   useEffect(() => {
 *     // KB 연동 플로우 시작
 *     kbConnectFlow.start();
 *   }, []);
 *   
 *   const handleComplete = async () => {
 *     try {
 *       const result = await connectKB(data);
 *       kbConnectFlow.success({ kb_id: result.id });
 *     } catch (error) {
 *       kbConnectFlow.fail('backend_5xx');
 *     }
 *   };
 * }
 * ```
 */

import { useIntent } from '../useIntent';
import { track } from '../../../../core/client';

export interface KBConnectFlowContext {
    kb_id?: number;
}

export interface KBConnectFlowActions {
    start: (options?: {
        parentIntentId?: string;
        parentIntentType?: string;
    }) => string;
    success: (context?: KBConnectFlowContext) => void;
    fail: (reason?: string) => void;
}

/**
 * 포트폴리오 생성 플로우 추적 훅
 */
export function useKBConnectFlow(): KBConnectFlowActions {
    const intent = useIntent<KBConnectFlowContext>({
        type: 'kb_connect_flow',
        requiredGate: 'login',
        onStart: ({ intent_id, parent_intent_id, required_gate }) => {
            track('kb_connect_flow_start', {
                intent_id,
                required_gate,
            });
            // console.log(`🎯 [KBConnectFlow] Start [intent_id: ${intent_id}]`);
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('kb_connect_flow_success', {
                intent_id,
                latency_ms,
            });
            // console.log(`✅ [KBConnectFlow] Success [intent_id: ${intent_id}, kb_id: ${context?.kb_id}]`);
        },
        onFail: ({ intent_id, fail_reason }) => {
            track('kb_connect_flow_fail', {
                intent_id,
                fail_reason,
            });
            // console.log(`❌ [KBConnectFlow] Fail [intent_id: ${intent_id}, reason: ${fail_reason}]`);
        },
    });



    return {
        start: (options) => intent.start(undefined, options),
        success: (context) => intent.success(),
        fail: (reason) => intent.fail(reason),
    };
}

